import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Container, Row, Col, Button, Spinner, Alert, Badge, Form, Toast, ToastContainer, Card, Modal, Dropdown, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import Select from 'react-select'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDateTime, formatCurrency } from '../utils/commonUtils'
import { usePermissions } from '../contexts/PermissionContext'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import AmazonProductExceptionModal, { formatAcos } from '../components/AmazonProductExceptionModal'
import AmazonAnomalyDetectionGuideModal from '../components/AmazonAnomalyDetectionGuideModal'
import '../styles/selectControl.css'
import '../styles/amazonAutomationCard.css'

const SEVERITY_COLORS = { critical: 'danger', warning: 'warning', info: 'info' }
const STATUS_COLORS = { open: 'primary', dismissed: 'secondary', not_useful: 'warning', resolved: 'light' }
const RUN_STATUS_COLORS = { success: 'success', skipped: 'secondary', failed: 'danger', running: 'warning' }

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'dismissed', label: 'Dismissed' },
    { value: 'not_useful', label: 'Not useful' },
    { value: 'resolved', label: 'Auto-closed' },
    { value: 'all', label: 'All statuses' },
]
// Problems are the daily list (critical + warning); opportunities and info are for the weekly review
const KIND_OPTIONS = [
    { value: 'problem', label: 'Problems' },
    { value: 'opportunity', label: 'Opportunities' },
    { value: 'info', label: 'Info' },
    { value: 'all', label: 'All kinds' },
]
const SEVERITY_OPTIONS = [
    { value: 'critical', label: 'Critical' },
    { value: 'warning', label: 'Warning' },
    { value: 'info', label: 'Info' },
]
const FEEDBACK_COMMENT_MAX = 2000
const FEEDBACK_COMMENT_PREVIEW = 60
const EMPTY_FEEDBACK = { show: false, status: '', alerts: [], reason: '', comment: '' }
// Dropdown menus in the table use fixed positioning so the table's overflow does not clip them;
// they stay in the themed DOM tree (no portal), which the dark theme styles rely on. They are
// rendered on mount (renderOnMount) so Popper measures them before the first open: a menu created
// on its first open is positioned at the viewport corner until something triggers an update.
// The menus must not stop click propagation: closing an open menu when another is clicked relies
// on the document-level "click outside" listener.
const FIXED_POPPER = { strategy: 'fixed' }
const HUB_PATH = '/amazon-ad-automation'
const BACK_LABEL = 'Back to Ad Automation'
const SETTINGS_PATH = '/amazon-ad-automation/settings'

function splitPath(path) {
    const [pathname, search] = (path || '').split('?')
    return { pathname, search: search ? `?${search}` : '' }
}

function AmazonAdAutomationDashboardScreen() {
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission } = usePermissions()
    const canManage = hasPermission('amazon.manage')

    const urlParams = new URLSearchParams(location.state?.returnFilters || location.search)

    const [automations, setAutomations] = useState([])
    const [automationsLoading, setAutomationsLoading] = useState(true)
    const [runningKey, setRunningKey] = useState('')

    const [summary, setSummary] = useState(null)
    const [alerts, setAlerts] = useState([])
    const [alertsLoading, setAlertsLoading] = useState(false)
    const [totalItems, setTotalItems] = useState(0)
    const [error, setError] = useState('')
    const [toast, setToast] = useState('')

    const [selectedAccountId, setSelectedAccountId] = useState(() => urlParams.get('account') || '')
    const [statusFilter, setStatusFilter] = useState(() => urlParams.get('status') || 'open')
    const [kindFilter, setKindFilter] = useState(() => urlParams.get('kind') || 'problem')
    const [severityFilter, setSeverityFilter] = useState(() => urlParams.get('severity') || '')
    const [showWeekly, setShowWeekly] = useState(false)
    const [feedbackReasons, setFeedbackReasons] = useState({ not_useful: [], dismissed: [] })
    const [feedback, setFeedback] = useState(EMPTY_FEEDBACK)
    const [feedbackError, setFeedbackError] = useState('')
    const [feedbackSaving, setFeedbackSaving] = useState(false)
    const feedbackRefs = useRef({})
    const [exceptionModal, setExceptionModal] = useState({ show: false, accountId: '', asin: '' })
    const [showAnomalyGuide, setShowAnomalyGuide] = useState(false)
    const feedRef = useRef(null)
    const [scrollToFeed, setScrollToFeed] = useState(false)
    const [weekly, setWeekly] = useState(null)
    const [weeklyLoading, setWeeklyLoading] = useState(false)
    const [sourceFilter, setSourceFilter] = useState(() => urlParams.get('source') || '')
    const [searchTerm, setSearchTerm] = useState(() => urlParams.get('search') || '')
    const [searchInputValue, setSearchInputValue] = useState(() => urlParams.get('search') || '')
    const [currentPage, setCurrentPage] = useState(() => parseInt(urlParams.get('page')) || 1)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [selectedIds, setSelectedIds] = useState([])
    const debounceRef = useRef(null)

    const accounts = automations[0]?.accounts || []
    const accountOptions = accounts.map(a => ({ value: String(a.account_id), label: a.account_name }))
    const sourceOptions = automations.map(a => ({ value: a.key, label: a.label }))

    // Restore filters passed back from a detail page
    useEffect(() => {
        if (location.state?.returnFilters) {
            navigate(`${location.pathname}${location.state.returnFilters}`, { replace: true, state: {} })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadAutomations = useCallback(async () => {
        try {
            const data = await amazonAPI.automations.getAll()
            setAutomations(data || [])
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setAutomationsLoading(false)
        }
    }, [])

    const loadSummary = useCallback(async () => {
        try {
            const data = await amazonAPI.alerts.getSummary(selectedAccountId)
            setSummary(data || null)
        } catch (err) {
            setError(formatErrorResponse(err))
        }
    }, [selectedAccountId])

    const loadAlerts = useCallback(async () => {
        setAlertsLoading(true)
        try {
            const params = { page: currentPage, page_size: pageSize, status: statusFilter }
            if (selectedAccountId) params.account_id = selectedAccountId
            if (kindFilter !== 'all') params.kind = kindFilter
            if (severityFilter) params.severity = severityFilter
            if (sourceFilter) params.source = sourceFilter
            if (searchTerm) params.search = searchTerm
            const data = await amazonAPI.alerts.getAll(params)
            setAlerts(data?.results || [])
            setTotalItems(data?.count || 0)
            setSelectedIds([])
            setError('')
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setAlertsLoading(false)
        }
    }, [currentPage, pageSize, statusFilter, selectedAccountId, kindFilter, severityFilter, sourceFilter, searchTerm])

    const loadWeekly = useCallback(async () => {
        if (!showWeekly) return
        setWeeklyLoading(true)
        try {
            setWeekly(await amazonAPI.alerts.getWeeklySummary(selectedAccountId))
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setWeeklyLoading(false)
        }
    }, [showWeekly, selectedAccountId])

    useEffect(() => { loadWeekly() }, [loadWeekly])
    // After a card badge narrows the feed, bring the feed into view so the change is visible
    useEffect(() => {
        if (scrollToFeed && !alertsLoading && feedRef.current) {
            feedRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setScrollToFeed(false)
        }
    }, [scrollToFeed, alertsLoading])
    useEffect(() => {
        amazonAPI.alerts.getFeedbackReasons()
            .then(data => data && setFeedbackReasons(data))
            .catch(err => setError(formatErrorResponse(err)))
    }, [])
    useEffect(() => { loadAutomations() }, [loadAutomations])
    useEffect(() => { loadSummary() }, [loadSummary])
    useEffect(() => { loadAlerts() }, [loadAlerts])

    const buildFilterString = useCallback(() => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (statusFilter !== 'open') params.set('status', statusFilter)
        if (kindFilter !== 'problem') params.set('kind', kindFilter)
        if (severityFilter) params.set('severity', severityFilter)
        if (sourceFilter) params.set('source', sourceFilter)
        if (searchTerm) params.set('search', searchTerm)
        if (currentPage > 1) params.set('page', currentPage)
        const s = params.toString()
        return s ? `?${s}` : ''
    }, [selectedAccountId, statusFilter, kindFilter, severityFilter, sourceFilter, searchTerm, currentPage])

    useEffect(() => {
        navigate(`${location.pathname}${buildFilterString()}`, { replace: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildFilterString])

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => { setSearchTerm(value); setCurrentPage(1) }, 500)
    }
    const handleSearchClear = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        setSearchInputValue(''); setSearchTerm(''); setCurrentPage(1)
    }

    const refreshAfterChange = async () => {
        await Promise.all([loadAlerts(), loadSummary(), loadAutomations(), loadWeekly()])
    }

    // Applies a decision to one alert, or to the selected alerts when ``alert`` is null, and owns the
    // toast and the refresh. Throws on failure so each caller shows the error where it belongs.
    const applyDecision = async (alert, newStatus, feedbackData = {}) => {
        let message
        if (alert) {
            await amazonAPI.alerts.setStatus(alert.id, newStatus, feedbackData)
            message = newStatus === 'open' ? 'Alert reopened' : newStatus === 'not_useful' ? 'Alert marked not useful' : 'Alert dismissed'
        } else {
            const result = await amazonAPI.alerts.bulkSetStatus(selectedIds, newStatus, feedbackData)
            const skipped = result?.skipped_auto_closed || 0
            const missing = result?.skipped_not_found || 0
            message = `${result?.updated ?? selectedIds.length} alert(s) ${newStatus === 'not_useful' ? 'marked not useful' : 'dismissed'}`
                + (skipped ? `, ${skipped} auto-closed skipped` : '')
                + (missing ? `, ${missing} no longer exist` : '')
        }
        setToast(message)
        await refreshAfterChange()
    }

    const handleSetStatus = async (alert, newStatus, feedbackData = {}) => {
        try {
            await applyDecision(alert, newStatus, feedbackData)
        } catch (err) {
            setError(err.response?.data?.error || formatErrorResponse(err))
        }
    }

    const handleBulkStatus = async (newStatus, feedbackData = {}) => {
        if (selectedIds.length === 0) return
        try {
            await applyDecision(null, newStatus, feedbackData)
        } catch (err) {
            setError(err.response?.data?.error || formatErrorResponse(err))
        }
    }

    // Feedback dialog: Not useful (reason required) and "Dismiss with comment" (reason optional).
    // ``alerts`` is the one alert of a row action, or empty for the selected alerts (bulk).
    const openFeedback = (newStatus, alert = null) => {
        setFeedback({ show: true, status: newStatus, alerts: alert ? [alert] : [], reason: '', comment: '' })
        setFeedbackError('')
    }
    const closeFeedback = () => { if (!feedbackSaving) setFeedback(EMPTY_FEEDBACK) }
    const focusFeedback = (field) => {
        const el = feedbackRefs.current[field]
        if (el && typeof el.focus === 'function') el.focus()
    }

    const submitFeedback = async (e) => {
        e.preventDefault()
        setFeedbackError('')
        const comment = feedback.comment.trim()
        if (feedback.status === 'not_useful' && !feedback.reason) {
            setFeedbackError('Choose why this alert is not useful'); focusFeedback('reason'); return
        }
        if (feedback.reason === 'other' && !comment) {
            setFeedbackError('Describe the reason in the comment'); focusFeedback('comment'); return
        }
        const payload = { reason: feedback.reason, comment }
        setFeedbackSaving(true)
        try {
            await applyDecision(feedback.alerts[0] || null, feedback.status, payload)
            setFeedback(EMPTY_FEEDBACK)
        } catch (err) {
            setFeedbackError(err.response?.data?.error || formatErrorResponse(err))
            if (err.response?.data?.field) focusFeedback(err.response.data.field)
        } finally {
            setFeedbackSaving(false)
        }
    }

    // Dismiss stays one click; the menu adds a reason or a comment
    const renderDismissButton = (onDismiss, onOpenComment, label = 'Dismiss', className = 'alert-action-btn') => (
        <Dropdown as={ButtonGroup} size="sm" className={className}>
            <Button variant="secondary" size="sm" onClick={() => onDismiss({})}>{label}</Button>
            <Dropdown.Toggle split variant="secondary" size="sm" aria-label="Dismiss with a reason" />
            <Dropdown.Menu popperConfig={FIXED_POPPER} renderOnMount>
                {feedbackReasons.dismissed.map(r => (
                    <Dropdown.Item key={r.value} onClick={() => onDismiss({ reason: r.value })}>{r.label}</Dropdown.Item>
                ))}
                <Dropdown.Divider />
                <Dropdown.Item onClick={onOpenComment}>Dismiss with comment…</Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    )

    const renderFeedbackNote = (fb) => {
        if (!fb) return null
        const comment = fb.comment || ''
        const short = comment.length > FEEDBACK_COMMENT_PREVIEW ? `${comment.slice(0, FEEDBACK_COMMENT_PREVIEW)}…` : comment
        return (
            <div className="small text-body-secondary" style={{ whiteSpace: 'normal' }}>
                {fb.reason_label && <div>{fb.reason_label}</div>}
                {comment && (
                    <OverlayTrigger placement="left" overlay={<Tooltip>{comment}</Tooltip>}>
                        <div className="fst-italic">&ldquo;{short}&rdquo;</div>
                    </OverlayTrigger>
                )}
            </div>
        )
    }

    const handleRun = async (automation, account) => {
        const key = `${automation.key}:${account.account_id}`
        setRunningKey(key)
        try {
            const run = await amazonAPI.automations.run(automation.key, account.account_id)
            if (run?.status === 'failed') {
                setError(`${automation.label} failed for ${account.account_name}: ${run.error_message}`)
            } else if (run?.status === 'skipped') {
                setToast(`${automation.label} skipped for ${account.account_name}: ${run.error_message}`)
            } else {
                setToast(`${automation.label} ran for ${account.account_name}: ${run.alerts_created} new, ${run.alerts_updated} updated, ${run.alerts_resolved} resolved`)
            }
            await refreshAfterChange()
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setRunningKey('')
        }
    }

    // Each automation's settings: Anomaly Detection -> its settings tab, Bidding -> the bidding page
    const openAutomationSettings = (automation, account) => {
        const { pathname, search } = splitPath(automation.settings_path)
        const query = new URLSearchParams(search)
        if (account) query.set('account', account.account_id)
        const qs = query.toString()
        navigate(`${pathname}${qs ? '?' + qs : ''}`, {
            state: {
                accountId: account?.account_id || '',
                accountName: account?.account_name || '',
                // Hub filters ride on backTo, not returnFilters: target pages read returnFilters as their own filters
                backTo: `${HUB_PATH}${buildFilterString()}`, backLabel: BACK_LABEL,
            },
        })
    }

    const hubState = () => ({ backTo: `${HUB_PATH}${buildFilterString()}`, backLabel: BACK_LABEL })

    const openSettingsTab = (tab, accountId = selectedAccountId) => {
        const params = new URLSearchParams({ tab })
        if (accountId) params.set('account', accountId)
        navigate(`${SETTINGS_PATH}?${params}`, { state: hubState() })
    }

    // Count badges on the cards narrow the feed to that automation, account and severity
    const filterFeed = (automationKey, account, severity = '') => {
        setSourceFilter(automationKey)
        setSelectedAccountId(String(account.account_id))
        setSeverityFilter(severity)
        setKindFilter(severity === 'critical' || severity === 'warning' ? 'problem' : 'all')
        setStatusFilter('open')
        setCurrentPage(1)
        setScrollToFeed(true)
    }

    const clearFilters = () => {
        setSelectedAccountId(''); setSourceFilter(''); setSeverityFilter(''); setKindFilter('problem')
        setStatusFilter('open'); setSearchInputValue(''); setSearchTerm(''); setCurrentPage(1)
    }

    const openAlertDetails = (alert) => {
        if (!alert.link_path) return
        const { pathname, search } = splitPath(alert.link_path)
        navigate(`${pathname}${search}`, {
            state: {
                accountId: alert.seller_account, accountName: alert.seller_account_name,
                // Hub filters ride on backTo, not returnFilters: target pages read returnFilters as their own filters
                backTo: `${HUB_PATH}${buildFilterString()}`, backLabel: BACK_LABEL,
            },
        })
    }

    const toggleSelected = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    }
    const selectableAlerts = alerts.filter(a => a.status !== 'resolved')
    const allOnPageSelected = selectableAlerts.length > 0 && selectableAlerts.every(a => selectedIds.includes(a.id))
    const toggleSelectAll = () => {
        setSelectedIds(allOnPageSelected ? [] : selectableAlerts.map(a => a.id))
    }

    const columns = [
        canManage && { id: 'select', header: (
            <Form.Check type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} aria-label="Select all" />
        ), cell: (row) => row.status === 'resolved' ? null : (
            <Form.Check type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelected(row.id)}
                onClick={(e) => e.stopPropagation()} aria-label={`Select alert ${row.id}`} />
        )},
        { id: 'severity', header: 'Severity', cell: (row) => (
            <Badge bg={SEVERITY_COLORS[row.severity] || 'secondary'} text={row.severity === 'warning' ? 'dark' : undefined}>
                {row.severity_label}
            </Badge>
        )},
        { id: 'account', header: 'Account', cell: (row) => row.seller_account_name || '' },
        { id: 'source', header: 'Automation', cell: (row) => (
            <div>
                <div>{row.source_label || ''}</div>
                {row.rule_label && <div className="small text-muted">{row.rule_label}</div>}
            </div>
        )},
        { id: 'title', header: 'Alert', cell: (row) => (
            <div>
                <div className="fw-semibold">{row.title}</div>
                {row.detail && <div className="small text-muted" style={{ whiteSpace: 'normal' }}>{row.detail}</div>}
                {row.payload?.related?.length > 0 && (
                    <div className="small mt-1" style={{ whiteSpace: 'normal' }}>
                        <span className="fw-semibold">Also seen on this product:</span>{' '}
                        {row.payload.related.map(r => r.title).join('; ')}
                    </div>
                )}
                {row.payload?.suppressed?.count > 0 && (
                    <div className="small mt-1" style={{ whiteSpace: 'normal' }}>
                        <span className="fw-semibold">Folded in {row.payload.suppressed.count} product alert(s):</span>{' '}
                        {row.payload.suppressed.products.map(p => p.asin).join(', ')}
                    </div>
                )}
                {row.payload?.demoted && (
                    <div className="small mt-1 text-body-secondary">
                        {row.payload.demoted === 'cap' ? 'Shown as info: past the daily cap' : 'Shown as info: below the weekly minimum'}
                    </div>
                )}
            </div>
        )},
        { id: 'entity', header: 'ASIN / Campaign', cell: (row) => (
            <div>
                {row.asin ? <div><CopyableText value={row.asin} toastLabel="ASIN" /></div> : null}
                {row.campaign_name ? <div className="small text-muted">{row.campaign_name}</div> : null}
                {row.product_exception && (
                    <Badge bg="info-subtle" text="info-emphasis" className="border border-info-subtle mt-1">
                        {row.product_exception.excluded ? 'Exception: excluded' : `Exception: target ${formatAcos(row.product_exception.target_acos)}`}
                    </Badge>
                )}
            </div>
        )},
        { id: 'impact', header: 'Impact', cell: (row) => row.impact_amount !== null && row.impact_amount !== undefined ? formatCurrency(row.impact_amount) : '' },
        { id: 'status', header: 'Status', cell: (row) => (
            <>
                <Badge bg={STATUS_COLORS[row.status] || 'secondary'} text={['resolved', 'not_useful'].includes(row.status) ? 'dark' : undefined}>{row.status_label}</Badge>
                {row.dismissed_by_username && <div className="small text-muted">{row.dismissed_by_username}</div>}
                {renderFeedbackNote(row.latest_feedback)}
            </>
        )},
        { id: 'firstSeen', header: 'First Seen', cell: (row) => formatDateTime(row.first_seen_at) },
        { id: 'lastSeen', header: 'Last Seen', cell: (row) => (
            <>
                {formatDateTime(row.last_seen_at)}
                {row.occurrences > 1 && <div className="small text-muted">{row.occurrences} times</div>}
            </>
        )},
        { id: 'actions', header: 'Actions', cell: (row) => (
            <div className="d-flex flex-wrap gap-1">
                {row.link_path && (
                    <Button variant="primary" size="sm" className="alert-action-btn" onClick={(e) => { e.stopPropagation(); openAlertDetails(row) }}>Details</Button>
                )}
                {canManage && row.status === 'open' && (
                    <>
                        {renderDismissButton(
                            (fb) => handleSetStatus(row, 'dismissed', fb),
                            () => openFeedback('dismissed', row),
                        )}
                        <Button variant="outline-warning" size="sm" className="alert-action-btn" onClick={(e) => { e.stopPropagation(); openFeedback('not_useful', row) }}>Not useful</Button>
                    </>
                )}
                {canManage && (row.status === 'dismissed' || row.status === 'not_useful') && (
                    <Button variant="outline-primary" size="sm" className="alert-action-btn" onClick={(e) => { e.stopPropagation(); handleSetStatus(row, 'open') }}>Reopen</Button>
                )}
                {row.source === 'anomaly' && row.asin && (
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" className="no-caret" aria-label="More actions">
                            <i className="fas fa-ellipsis-h"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu popperConfig={FIXED_POPPER} renderOnMount>
                            <Dropdown.Item onClick={() => setExceptionModal({ show: true, accountId: String(row.seller_account), asin: row.asin })}>
                                Product exception…
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </div>
        )},
    ].filter(Boolean)

    const renderAccountRow = (automation, account) => {
        const run = account.last_run
        const key = `${automation.key}:${account.account_id}`
        return (
            <div className="account-row" key={account.account_id}>
                <div className="account-name">
                    {account.account_name}
                    <div className="run-meta">
                        {run ? (
                            <>
                                <Badge bg={RUN_STATUS_COLORS[run.status] || 'secondary'} className="me-1">{run.status}</Badge>
                                {formatDateTime(run.finished_at || run.started_at)}
                                {run.trigger === 'manual' ? ' (manual)' : ''}
                            </>
                        ) : 'Never run'}
                        {!account.scheduled && (
                            <>
                                {' · '}
                                <Button variant="link" size="sm" className="p-0 align-baseline run-meta-link"
                                    onClick={() => openSettingsTab('general', String(account.account_id))}>
                                    schedule off
                                </Button>
                            </>
                        )}
                        {['skipped', 'success'].includes(run?.status) && run.error_message && (
                            <div className="text-body-secondary">{run.error_message}</div>
                        )}
                    </div>
                </div>
                <div className="row-right">
                    {[
                        ['critical', account.open_critical, 'danger', undefined],
                        ['warning', account.open_warning, 'warning', 'dark'],
                        ['info', account.open_info, 'info', 'dark'],
                    ].filter(([, count]) => count > 0).map(([severity, count, bg, text]) => (
                        <button key={severity} type="button" className="badge-link"
                            title={`Show ${count} open ${severity} alert(s)`}
                            onClick={() => filterFeed(automation.key, account, severity)}>
                            <Badge bg={bg} text={text}>{count}</Badge>
                        </button>
                    ))}
                    {account.open_total === 0 && (
                        <button type="button" className="badge-link" title="No open alerts: show this automation's alerts"
                            onClick={() => filterFeed(automation.key, account)}>
                            <Badge bg="success">0</Badge>
                        </button>
                    )}
                    <Button variant="outline-primary" size="sm" onClick={() => openAutomationSettings(automation, account)}>
                        Settings
                    </Button>
                    {canManage && automation.manual_supported && (
                        <Button variant="primary" size="sm" disabled={runningKey !== ''} onClick={() => handleRun(automation, account)}>
                            {runningKey === key ? <Spinner size="sm" /> : 'Run'}
                        </Button>
                    )}
                </div>
            </div>
        )
    }

    const selectStyle = { minWidth: '180px' }

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                <h1 className="mb-0">Ad Automation</h1>
                <Button variant="outline-primary" onClick={() => openSettingsTab('anomaly')}>
                    <i className="fas fa-gear me-2"></i>Settings
                </Button>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            {!automationsLoading && accounts.length > 0 && accounts.every(a => !a.scheduled) && (
                <Alert variant="warning">
                    Daily runs are off for all accounts, so alerts only update when someone clicks Run.{' '}
                    <Alert.Link as={Link} to={`${SETTINGS_PATH}?tab=general${accounts[0] ? `&account=${accounts[0].account_id}` : ''}`}
                        state={hubState()}>
                        Turn them on in Settings → General
                    </Alert.Link>.
                </Alert>
            )}

            {automationsLoading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <Row className="g-4 mb-4">
                    {automations.map(automation => (
                        <Col lg={4} md={6} key={automation.key}>
                            <div className="automation-card">
                                <div className="icon-rail"><i className={automation.icon}></i></div>
                                <div className="card-content">
                                    <div className="top-row">
                                        <div className="title-block">
                                            <h5>{automation.label}</h5>
                                            <p className="desc">{automation.description}</p>
                                            {automation.key === 'anomaly' && (
                                                <Button variant="link" size="sm" className="p-0" onClick={() => setShowAnomalyGuide(true)}>
                                                    <i className="fas fa-circle-question me-1"></i>How it works
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="account-rows">
                                        {automation.accounts.map(account => renderAccountRow(automation, account))}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

            <h4 className="mb-3" ref={feedRef} style={{ scrollMarginTop: '80px' }}>Needs Attention</h4>

            {summary && (
                <Row className="mb-3 g-2">
                    <Col><div className="bg-danger text-white py-2 px-2 rounded text-center h-100 d-flex flex-column justify-content-center"><div className="small fw-semibold">Open Critical</div><h4 className="mb-0">{summary.open_critical}</h4></div></Col>
                    <Col><div className="bg-warning text-white py-2 px-2 rounded text-center h-100 d-flex flex-column justify-content-center"><div className="small fw-semibold">Open Warning</div><h4 className="mb-0">{summary.open_warning}</h4></div></Col>
                    <Col><div className="bg-primary text-white py-2 px-2 rounded text-center h-100 d-flex flex-column justify-content-center"><div className="small fw-semibold">Open Total</div><h4 className="mb-0">{summary.open_total}</h4></div></Col>
                    <Col><div className="bg-success text-white py-2 px-2 rounded text-center h-100 d-flex flex-column justify-content-center"><div className="small fw-semibold">Dismissed Today</div><h4 className="mb-0">{summary.dismissed_today}</h4></div></Col>
                    <Col><div className="bg-warning text-white py-2 px-2 rounded text-center h-100 d-flex flex-column justify-content-center"><div className="small fw-semibold">Not Useful Today</div><h4 className="mb-0">{summary.not_useful_today}</h4></div></Col>
                    <Col><div className="bg-secondary text-white py-2 px-2 rounded text-center h-100 d-flex flex-column justify-content-center"><div className="small fw-semibold">Auto-closed Today</div><h4 className="mb-0">{summary.resolved_today}</h4></div></Col>
                    <Col><div className="bg-info text-white py-2 px-2 rounded text-center h-100 d-flex flex-column justify-content-center"><div className="small fw-semibold">Opportunities</div><h4 className="mb-0">{summary.open_opportunities ?? 0}</h4></div></Col>
                </Row>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div style={selectStyle}>
                    <Select
                        value={accountOptions.find(o => o.value === selectedAccountId) || null}
                        onChange={(opt) => { setSelectedAccountId(opt?.value || ''); setCurrentPage(1) }}
                        options={accountOptions}
                        placeholder="All accounts"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
                <div style={selectStyle}>
                    <Select
                        value={STATUS_OPTIONS.find(o => o.value === statusFilter) || STATUS_OPTIONS[0]}
                        onChange={(opt) => { setStatusFilter(opt?.value || 'open'); setCurrentPage(1) }}
                        options={STATUS_OPTIONS}
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
                <div style={selectStyle}>
                    <Select
                        value={KIND_OPTIONS.find(o => o.value === kindFilter) || KIND_OPTIONS[0]}
                        onChange={(opt) => { setKindFilter(opt?.value || 'problem'); setCurrentPage(1) }}
                        options={KIND_OPTIONS}
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
                <div style={selectStyle}>
                    <Select
                        value={SEVERITY_OPTIONS.find(o => o.value === severityFilter) || null}
                        onChange={(opt) => {
                            setSeverityFilter(opt?.value || '')
                            // Problems are critical + warning only; picking Info would show nothing
                            if (opt?.value === 'info' && kindFilter === 'problem') setKindFilter('all')
                            setCurrentPage(1)
                        }}
                        options={SEVERITY_OPTIONS}
                        placeholder="All severities"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
                <div style={selectStyle}>
                    <Select
                        value={sourceOptions.find(o => o.value === sourceFilter) || null}
                        onChange={(opt) => { setSourceFilter(opt?.value || ''); setCurrentPage(1) }}
                        options={sourceOptions}
                        placeholder="All automations"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
                <div style={{ minWidth: '240px', flex: '1 1 240px' }}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleSearchClear}
                        placeholder="Search title, ASIN, SKU, campaign..."
                    />
                </div>
                {canManage && selectedIds.length > 0 && (
                    <div className="d-flex gap-2 ms-auto">
                        {renderDismissButton(
                            (fb) => handleBulkStatus('dismissed', fb),
                            () => openFeedback('dismissed'),
                            `Dismiss ${selectedIds.length} selected`,
                            '',
                        )}
                        <Button variant="outline-warning" size="sm" onClick={() => openFeedback('not_useful')}>
                            Mark {selectedIds.length} not useful
                        </Button>
                    </div>
                )}
            </div>

            {(() => {
                const chips = [
                    selectedAccountId && { key: 'account', label: accountOptions.find(o => o.value === selectedAccountId)?.label || 'Account', clear: () => setSelectedAccountId('') },
                    sourceFilter && { key: 'source', label: sourceOptions.find(o => o.value === sourceFilter)?.label || sourceFilter, clear: () => setSourceFilter('') },
                    severityFilter && { key: 'severity', label: SEVERITY_OPTIONS.find(o => o.value === severityFilter)?.label || severityFilter, clear: () => setSeverityFilter('') },
                    kindFilter !== 'problem' && { key: 'kind', label: KIND_OPTIONS.find(o => o.value === kindFilter)?.label || kindFilter, clear: () => setKindFilter('problem') },
                    statusFilter !== 'open' && { key: 'status', label: STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || statusFilter, clear: () => setStatusFilter('open') },
                    searchTerm && { key: 'search', label: `"${searchTerm}"`, clear: handleSearchClear },
                ].filter(Boolean)
                if (!chips.length) return null
                return (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        <span className="small text-body-secondary">Showing:</span>
                        {chips.map(chip => (
                            <span key={chip.key} className="badge rounded-pill bg-primary-subtle text-primary-emphasis border border-primary-subtle d-inline-flex align-items-center gap-1">
                                {chip.label}
                                <button type="button" className="btn-close btn-close-sm" style={{ fontSize: '0.55rem' }}
                                    aria-label={`Remove filter ${chip.label}`}
                                    onClick={() => { chip.clear(); setCurrentPage(1) }} />
                            </span>
                        ))}
                        <Button variant="link" size="sm" className="p-0" onClick={clearFilters}>Clear all</Button>
                    </div>
                )
            })()}

            {alertsLoading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : alerts.length === 0 ? (
                <Alert variant="success" className="mb-0">
                    {statusFilter === 'open' && !severityFilter && !sourceFilter && !searchTerm
                        ? 'Nothing needs attention right now.'
                        : 'No alerts match the current filters.'}
                </Alert>
            ) : (
                <>
                    <div className="table-responsive">
                        <ResizableTable
                            columns={columns}
                            data={alerts}
                            tableId="amazon_alerts_feed"
                            minHeight="auto"
                            striped
                            hover
                            className="table-sm"
                        />
                    </div>
                    <div className="mt-3">
                        <PaginationComponent
                            currentPage={currentPage}
                            totalItems={totalItems}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                        />
                    </div>
                </>
            )}

            <Card className="mt-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Weekly summary</strong>
                    <Button variant="outline-primary" size="sm" onClick={() => setShowWeekly(v => !v)}>
                        <i className={`fas fa-chevron-${showWeekly ? 'up' : 'down'} me-2`}></i>{showWeekly ? 'Hide' : 'Show'}
                    </Button>
                </Card.Header>
                {showWeekly && (
                    <Card.Body>
                        <p className="small text-body-secondary">
                            Minor items kept off the daily list (open info alerts seen in the last 7 days) and the best opportunities.
                        </p>
                        {weeklyLoading ? (
                            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                        ) : !weekly || (weekly.groups.length === 0 && weekly.opportunities.length === 0) ? (
                            <div className="text-body-secondary">Nothing minor this week.</div>
                        ) : (
                            <>
                                {weekly.groups.map(group => (
                                    <div key={group.rule_key} className="mb-3">
                                        <div className="d-flex flex-wrap gap-2 align-items-baseline">
                                            <span className="fw-semibold">{group.rule_label}</span>
                                            <span className="small text-body-secondary">{group.source_label}</span>
                                            <Badge bg="info" text="dark">{group.count}</Badge>
                                            {group.total_impact > 0 && <span className="small">{formatCurrency(group.total_impact)} per week</span>}
                                            {Object.entries(group.demoted).map(([label, count]) => (
                                                <span key={label} className="small text-body-secondary">· {label}: {count}</span>
                                            ))}
                                        </div>
                                        <ul className="small mb-0">
                                            {group.top.map(item => (
                                                <li key={item.id}>
                                                    {item.title}
                                                    {item.impact_amount !== null && item.impact_amount !== undefined ? ` (${formatCurrency(item.impact_amount)}/week)` : ''}
                                                    {item.link_path && (
                                                        <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={() => openAlertDetails(item)}>Details</Button>
                                                    )}
                                                </li>
                                            ))}
                                            {group.count > group.top.length && (
                                                <li className="text-body-secondary">and {group.count - group.top.length} more (Kind: Info)</li>
                                            )}
                                        </ul>
                                    </div>
                                ))}
                                {weekly.opportunities.length > 0 && (
                                    <div>
                                        <div className="fw-semibold mb-1">Top opportunities</div>
                                        <ul className="small mb-0">
                                            {weekly.opportunities.map(item => (
                                                <li key={item.id}>
                                                    {item.title}
                                                    {item.impact_amount !== null && item.impact_amount !== undefined ? ` (about ${formatCurrency(item.impact_amount)}/week)` : ''}
                                                    {item.link_path && (
                                                        <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={() => openAlertDetails(item)}>Details</Button>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </Card.Body>
                )}
            </Card>

            <AmazonAnomalyDetectionGuideModal
                show={showAnomalyGuide}
                onHide={() => setShowAnomalyGuide(false)}
                accountId={selectedAccountId || (accounts[0] ? String(accounts[0].account_id) : '')}
                accountName={(accountOptions.find(o => o.value === selectedAccountId) || accountOptions[0])?.label || ''}
            />

            <AmazonProductExceptionModal
                show={exceptionModal.show}
                accountId={exceptionModal.accountId}
                asin={exceptionModal.asin}
                canManage={canManage}
                onClose={() => setExceptionModal({ show: false, accountId: '', asin: '' })}
                onSaved={() => { setToast('Product exception saved; it applies from the next run'); loadAlerts() }}
            />

            <Modal show={feedback.show} onHide={closeFeedback} size="lg" backdrop="static" keyboard={false}>
                <Form onSubmit={submitFeedback}>
                    <Modal.Header closeButton>
                        <Modal.Title>{feedback.status === 'not_useful' ? 'Mark not useful' : 'Dismiss with comment'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-body-secondary mb-3">
                            {feedback.alerts.length === 1 ? feedback.alerts[0].title : `${selectedIds.length} selected alerts`}
                        </p>
                        <Form.Group className="mb-3">
                            <Form.Label>{feedback.status === 'not_useful' ? 'Why is this not useful?' : 'Reason (optional)'}</Form.Label>
                            {feedback.status === 'dismissed' && (
                                <Form.Check
                                    type="radio" id="feedback-reason-none" name="feedback-reason"
                                    label="No reason"
                                    checked={feedback.reason === ''}
                                    onChange={() => { setFeedback(f => ({ ...f, reason: '' })); setFeedbackError('') }}
                                />
                            )}
                            {(feedbackReasons[feedback.status] || []).map((r, i) => (
                                <Form.Check
                                    key={r.value} type="radio" id={`feedback-reason-${r.value}`} name="feedback-reason"
                                    label={r.label}
                                    checked={feedback.reason === r.value}
                                    ref={i === 0 ? (el => { feedbackRefs.current.reason = el }) : undefined}
                                    onChange={() => { setFeedback(f => ({ ...f, reason: r.value })); setFeedbackError('') }}
                                />
                            ))}
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Comment{feedback.reason === 'other' ? '' : ' (optional)'}</Form.Label>
                            <Form.Control
                                as="textarea" rows={3} maxLength={FEEDBACK_COMMENT_MAX}
                                value={feedback.comment}
                                ref={el => { feedbackRefs.current.comment = el }}
                                placeholder={feedback.status === 'not_useful'
                                    ? 'What made this not useful? e.g. "promotion week, high ACoS expected"'
                                    : 'Anything worth knowing about this decision'}
                                onChange={e => { const comment = e.target.value; setFeedback(f => ({ ...f, comment })); setFeedbackError('') }}
                            />
                            <Form.Text>
                                Used to tune the rules. {feedback.comment.length}/{FEEDBACK_COMMENT_MAX}
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="d-block">
                        {feedbackError && <Alert variant="danger" className="mb-2">{feedbackError}</Alert>}
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="outline-primary" onClick={closeFeedback} disabled={feedbackSaving}>Cancel</Button>
                            <Button type="submit" variant={feedback.status === 'not_useful' ? 'warning' : 'primary'} disabled={feedbackSaving}>
                                {feedbackSaving ? <Spinner size="sm" /> : feedback.status === 'not_useful' ? 'Mark not useful' : 'Dismiss'}
                            </Button>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
                <Toast show={!!toast} onClose={() => setToast('')} delay={3000} autohide bg="success">
                    <Toast.Body className="text-white">{toast}</Toast.Body>
                </Toast>
            </ToastContainer>
        </Container>
    )
}

export default AmazonAdAutomationDashboardScreen
